import { forwardRef } from "react";
import { createTheme, alpha } from "@mui/material";
import { LinkProps } from "@mui/material/Link";
import { Link as RouterLink, LinkProps as RouterLinkProps } from "react-router-dom";

const LinkBehavior = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }>(
	(props, ref) => {
		const { href, ...other } = props;
		return <RouterLink ref={ref} to={href} {...other} />;
	}
);

export const theme = createTheme({
	palette: {
		primary: {
			main: "#5b4ebf",
			light: "#d7d2ff",
			dark: "#403685"
		},
		secondary: {
			main: "#112c34"
		},
		success: {
			main: "#30ae3d",
			contrastText: "white",
			light: "#00FF4736"
		},
		error: {
			main: "#d32f2f",
			light: "#FFDDDD"
		},
		warning: {
			main: "#EDA438",
			contrastText: "white",
			light: "#F7D5A130"
		},
		info: {
			main: "#388dff",
			contrastText: "white",
			light: "#2196F312",
			dark: "#01579B"
		},
		text: {
			primary: "#3D3D3D"
		},
		background: {
			default: "#f8f9fa",
			paper: "#ffffff"
		}
	},
	breakpoints: {
		values: {
			xs: 0,
			sm: 576,
			md: 768,
			lg: 992,
			xl: 1200
		}
	},
	typography: {
		fontFamily: `"Inter", "Roboto", "Helvetica", "Arial", sans-serif`,
		h1: {
			fontWeight: 700,
			letterSpacing: "-0.02em"
		},
		h2: {
			fontWeight: 600,
			letterSpacing: "-0.01em"
		},
		h3: {
			fontWeight: 600,
			letterSpacing: "-0.01em"
		},
		h4: {
			fontWeight: 600,
			letterSpacing: "-0.01em"
		},
		h5: {
			fontWeight: 600,
			letterSpacing: "-0.01em"
		},
		h6: {
			fontWeight: 600,
			letterSpacing: "-0.01em"
		},
		body1: {
			lineHeight: 1.6
		},
		body2: {
			lineHeight: 1.5
		}
	},
	shape: {
		borderRadius: 12
	},
	shadows: [
		"none",
		"0px 2px 4px rgba(0, 0, 0, 0.05)",
		"0px 4px 8px rgba(0, 0, 0, 0.08)",
		"0px 8px 16px rgba(0, 0, 0, 0.12)",
		"0px 16px 32px rgba(0, 0, 0, 0.16)",
		"0px 32px 64px rgba(0, 0, 0, 0.20)",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none"
	],
	components: {
		MuiLink: {
			defaultProps: {
				component: LinkBehavior
			} as LinkProps
		},
		MuiButtonBase: {
			defaultProps: {
				LinkComponent: LinkBehavior
			}
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: "none",
					borderRadius: 6,
					boxShadow: "none",
					transition: "all 0.2s ease-in-out",
					"&:hover": {
						boxShadow: "0px 4px 12px rgba(91, 78, 191, 0.3)",
						transform: "translateY(-1px)"
					},
					"&:active": {
						transform: "translateY(0)"
					},
					"&.Mui-disabled": {
						color: "#fff",
						background: "#d1d5db",
						opacity: 1
					}
				},
				containedPrimary: {
					background: "#5b4ebf",
					"&:hover": {
						background: "#403685"
					},
					"&.Mui-disabled": {
						background: "#d7d2ff",
						color: "#fff",
						opacity: 1
					}
				},
				containedSecondary: {
					background: "#112c34",
					color: "white",
					"&:hover": {
						background: "#27496d"
					},
					"&.Mui-disabled": {
						background: "#b0bec5",
						color: "#fff",
						opacity: 1
					}
				},
				containedSuccess: {
					background: "#30ae3d",
					color: "white",
					"&:hover": {
						background: "#1e7d2c"
					},
					"&.Mui-disabled": {
						background: "#b9e4c9",
						color: "#fff",
						opacity: 1
					}
				},
				containedError: {
					background: "#d32f2f",
					color: "white",
					"&:hover": {
						background: "#a31515"
					},
					"&.Mui-disabled": {
						background: "#f8bbbc",
						color: "#fff",
						opacity: 1
					}
				},
				containedWarning: {
					background: "#EDA438",
					color: "white",
					"&:hover": {
						background: "#b97a1b"
					},
					"&.Mui-disabled": {
						background: "#ffe0b2",
						color: "#fff",
						opacity: 1
					}
				},
				containedInfo: {
					background: "#388dff",
					color: "white",
					"&:hover": {
						background: "#01579B"
					},
					"&.Mui-disabled": {
						background: "#b3e5fc",
						color: "#fff",
						opacity: 1
					}
				}
			}
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 12,
					boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
					border: "1px solid rgba(0, 0, 0, 0.04)",
					transition: "all 0.3s ease-in-out",
					"&:hover": {
						boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
						transform: "translateY(-2px)"
					}
				}
			}
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)"
				},
				elevation1: {
					boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)"
				},
				elevation2: {
					boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)"
				},
				elevation3: {
					boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)"
				}
			}
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: 6,
						transition: "all 0.2s ease-in-out",
						background: "rgba(255, 255, 255, 0.8)",
						"&:hover": {
							background: "rgba(255, 255, 255, 0.9)",
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: "#5b4ebf"
							}
						},
						"&.Mui-focused": {
							background: "rgba(255, 255, 255, 0.95)",
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: "#5b4ebf",
								borderWidth: 2
							}
						}
					},
					"& .MuiFilledInput-root": {
						borderRadius: 6,
						transition: "all 0.2s ease-in-out",
						"&:hover": {
							backgroundColor: alpha("#5b4ebf", 0.04)
						},
						"&.Mui-focused": {
							backgroundColor: alpha("#5b4ebf", 0.08)
						},
						border: "1px solid rgba(0, 0, 0, 0.08)"
					}
				}
			}
		},
		MuiMenu: {
			styleOverrides: {
				paper: {
					borderRadius: 8,
					boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
					border: "1px solid rgba(0, 0, 0, 0.08)",
					marginTop: 2
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					borderRadius: 6,
					margin: "1px 4px",
					padding: "6px 12px",
					transition: "all 0.2s ease-in-out",
					"&:hover": {
						backgroundColor: alpha("#5b4ebf", 0.08)
					}
				}
			}
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					borderRadius: 6,
					margin: "1px 0",
					transition: "all 0.2s ease-in-out",
					"&:hover": {
						backgroundColor: alpha("#5b4ebf", 0.08)
					},
					"&.Mui-selected": {
						backgroundColor: alpha("#5b4ebf", 0.12),
						"&:hover": {
							backgroundColor: alpha("#5b4ebf", 0.16)
						}
					}
				}
			}
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					backgroundColor: "#ffffff",
					color: "#3D3D3D",
					boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
					borderBottom: "1px solid rgba(0, 0, 0, 0.08)"
				}
			}
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {
					backgroundColor: "#ffffff",
					borderRight: "1px solid rgba(0, 0, 0, 0.08)",
					boxShadow: "4px 0px 16px rgba(0, 0, 0, 0.08)"
				}
			}
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: "rgba(0, 0, 0, 0.08)"
				}
			}
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderRadius: 6,
					fontWeight: 500,
					"&.MuiChip-colorPrimary": {
						backgroundColor: alpha("#5b4ebf", 0.12),
						color: "#5b4ebf"
					},
					"&.MuiChip-colorSuccess": {
						backgroundColor: alpha("#30ae3d", 0.12),
						color: "#30ae3d"
					},
					"&.MuiChip-colorWarning": {
						backgroundColor: alpha("#EDA438", 0.12),
						color: "#EDA438"
					},
					"&.MuiChip-colorError": {
						backgroundColor: alpha("#d32f2f", 0.12),
						color: "#d32f2f"
					}
				}
			}
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					borderRadius: 6,
					transition: "all 0.2s ease-in-out",
					"&:hover": {
						backgroundColor: alpha("#5b4ebf", 0.08),
						transform: "scale(1.05)"
					}
				}
			}
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					backgroundColor: "rgba(44, 44, 44, 0.8)",
					borderRadius: 6,
					fontSize: "0.875rem",
					padding: "6px 10px"
				},
				arrow: {
					color: "rgba(44, 44, 44, 0.8)"
				}
			}
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					borderRadius: 12,
					boxShadow: "0px 16px 48px rgba(0, 0, 0, 0.16)"
				}
			}
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					fontWeight: 600,
					padding: "16px 20px 12px 20px"
				}
			}
		},
		MuiDialogContent: {
			styleOverrides: {
				root: {
					padding: "12px 20px 20px 20px"
				}
			}
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					"& .MuiTabs-indicator": {
						backgroundColor: "#5b4ebf",
						height: 3,
						borderRadius: "3px 3px 0 0"
					}
				}
			}
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: "none",
					fontWeight: 600,
					minHeight: 40,
					"&.Mui-selected": {
						color: "#5b4ebf"
					}
				}
			}
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					borderRadius: "0.5rem"
				},
				outlined: {
					background: "rgba(255, 255, 255, 0.8)",
					"&:hover": {
						background: "rgba(255, 255, 255, 0.9)"
					},
					"&.Mui-focused": {
						background: "rgba(255, 255, 255, 0.95)"
					}
				}
			}
		}
	}
});
